import { StateCreator } from "zustand";

// --- State Type ---
export type OnboardingSliceState = {
  /** Map of programId -> current step index */
  currentStepByProgram: Record<number, number>;
  /** Map of programId -> array of skipped step indices */
  skippedStepsByProgram: Record<number, number[]>;
  /** Map of programId -> last quiz question index (for restoring position) */
  quizQuestionIndexByProgram: Record<number, number>;
};

// --- Actions Type ---
export type OnboardingSliceActions = {
  /** Set the current step for a program */
  setCurrentStep: (programId: number, step: number) => void;
  /** Mark a step as skipped for a program */
  skipStep: (programId: number, stepIndex: number) => void;
  /** Set the quiz question index for a program (for restoring position) */
  setQuizQuestionIndex: (programId: number, index: number) => void;
  /** Reset onboarding progress for a program */
  resetOnboarding: (programId: number) => void;
  /** Clear all onboarding progress */
  clearAllOnboarding: () => void;
};

// --- Combined Type ---
export type OnboardingSlice = OnboardingSliceState & OnboardingSliceActions;

// --- Initial State ---
export const initialOnboardingState: OnboardingSliceState = {
  currentStepByProgram: {},
  skippedStepsByProgram: {},
  quizQuestionIndexByProgram: {},
};

// --- Slice Creator ---
export const createOnboardingSlice: StateCreator<
  OnboardingSlice,
  [],
  [],
  OnboardingSlice
> = (set) => ({
  ...initialOnboardingState,

  setCurrentStep: (programId, step) =>
    set((state) => ({
      currentStepByProgram: {
        ...state.currentStepByProgram,
        [programId]: step,
      },
    })),

  skipStep: (programId, stepIndex) =>
    set((state) => {
      const currentSkipped = state.skippedStepsByProgram[programId] ?? [];
      // Avoid duplicates
      if (currentSkipped.includes(stepIndex)) {
        return state;
      }
      return {
        skippedStepsByProgram: {
          ...state.skippedStepsByProgram,
          [programId]: [...currentSkipped, stepIndex],
        },
      };
    }),

  setQuizQuestionIndex: (programId, index) =>
    set((state) => ({
      quizQuestionIndexByProgram: {
        ...state.quizQuestionIndexByProgram,
        [programId]: index,
      },
    })),

  resetOnboarding: (programId) =>
    set((state) => ({
      currentStepByProgram: {
        ...state.currentStepByProgram,
        [programId]: 0,
      },
      skippedStepsByProgram: {
        ...state.skippedStepsByProgram,
        [programId]: [],
      },
      quizQuestionIndexByProgram: {
        ...state.quizQuestionIndexByProgram,
        [programId]: 0,
      },
    })),

  clearAllOnboarding: () =>
    set({
      currentStepByProgram: {},
      skippedStepsByProgram: {},
      quizQuestionIndexByProgram: {},
    }),
});

// --- Selectors ---
export const onboarding = {
  /** Get current step for a program */
  currentStep: (programId: number) => (s: OnboardingSlice) =>
    s.currentStepByProgram[programId] ?? 0,

  /** Get skipped steps for a program */
  skippedSteps: (programId: number) => (s: OnboardingSlice) =>
    s.skippedStepsByProgram[programId] ?? [],

  /** Check if a specific step is skipped */
  isStepSkipped:
    (programId: number, stepIndex: number) => (s: OnboardingSlice) =>
      (s.skippedStepsByProgram[programId] ?? []).includes(stepIndex),

  /** Get quiz question index for a program */
  quizQuestionIndex: (programId: number) => (s: OnboardingSlice) =>
    s.quizQuestionIndexByProgram[programId] ?? 0,

  /** Get all onboarding state and actions */
  state: (s: OnboardingSlice) => ({
    currentStepByProgram: s.currentStepByProgram,
    skippedStepsByProgram: s.skippedStepsByProgram,
    quizQuestionIndexByProgram: s.quizQuestionIndexByProgram,
    setCurrentStep: s.setCurrentStep,
    skipStep: s.skipStep,
    setQuizQuestionIndex: s.setQuizQuestionIndex,
    resetOnboarding: s.resetOnboarding,
    clearAllOnboarding: s.clearAllOnboarding,
  }),
};

